import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@core/services/core/auth/auth.service';
import { BaseService } from '@core/services/core/base.service';
import { ContextService } from '@core/services/core/context.service';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { OperationRegisterService } from '@core/services/core/operation-register.service';
import { AddressHelperService } from '@core/services/helpers/address-helper.service';
import { FileHelperService } from '@core/services/helpers/file-helper.service';
import { Platform } from '@ionic/angular';
import { PatrimonialIdentification } from '@models/patrimonial';
import { LANGUAGE, SMS } from '@modules/receipt-payment/receipt-payment-constants';
import {
  APP_KEY_PIN_SMS, GetUserDataResponse, PostUserDataResponse, RegisterApiPath,
  RegisterAttempts, RegisterDocumentTypeOptions, RegisterGenderOptions, RegisterLoadingId, RegisterResponse, RegisterRicProfileRequest, RegisterServiceCodes,
  RegisterState,
  RegisterUserHttpResponse, RegisterValidatePinEmailResponse, RegisterValidatePinSMSResponse, REGISTER_ENTITY_ID, REGISTER_GROUP_CODE, REGISTER_PORTAL_ID, ValidateEmailResponse
} from '@modules/register/models/register';
import { TranslateService } from '@ngx-translate/core';
import { spinner } from '@utils/rxjs';
import { isAndroidApp } from '@utils/utils';
import * as moment from 'moment';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RegisterService {

  private readonly catchError;

  constructor(
    private base: BaseService,
    private ro: OperationRegisterService,
    private platform: Platform,
    private mmobileHelper: MMobileHelperService,
    private translate: TranslateService,
    private mmHelper: MMobileHelperService,
    private ctx: ContextService,
    private auth: AuthService,
    private fileHelper: FileHelperService,
    private http: HttpClient,
    private addressHelper: AddressHelperService,
  ) {
    this.catchError = catchError((err: HttpErrorResponse) => {
      return of({
        status: err.status,
        code: err?.error?.extraData?.code
      });
    });
  }


  getSsoUrl(product: string, state: Partial<RegisterState>) {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/sso/sessionUrl`;
    const params = {
      roEntityCode: '00',
      roGroupingCode: '0',
      roProductId: product,
      roOrigin: `${this.ro.getOrigin()}`,
      roEmail: state.email,
      roDocumentNumber: state.documentNumber
    };

    return this.http.get<{ url: string; }>(url, {
      ...this.base.getPrivateOIMHeaders(),
      params
    }).pipe(
      map(data => {
        const origin = this.ctx.isApp ? 'metsapp' : 'metsweb';
        let urlRes = `${data.url}?origin=${origin}`;

        if (state.hashTC) {
          urlRes += `&hashTC=${state.hashTC}`;
        }

        if (state.channel) {
          urlRes += `&channel=${state.channel}`;
        }

        if (state.subchannel) {
          urlRes += `&subchannel=${state.subchannel}`;
        }

        return urlRes;
      })
    );
  }

  getCampaignId(state: Partial<RegisterState>) {
    const path = RegisterApiPath.CAMPAIGN_ID.replace('[ID]', this.getServiceCode(state));
    const url = this.getUrl(path, 'middlewarePublicServices');
    const headers = this.base.getPublicHeaders();
    return this.http.get<string>(url, { ...headers }).pipe(
      spinner(RegisterLoadingId.VALIDATE_INITIAL_DATA));
  }

  incomingCallRA(state: Partial<RegisterState>) {
    const payload = this.getIncomingCallRAPayload(state);
    const path = RegisterApiPath.INCOMING_CALL;
    const url = this.getUrl(path, 'middlewarePublicServices');
    const headers = this.base.getPublicHeaders();
    return this.http.post<ValidateEmailResponse>(url, payload, { ...headers })
      .pipe(
        spinner(RegisterLoadingId.INCOMING_CALL));
  }

  validateEmail(state: Partial<RegisterState>): Observable<ValidateEmailResponse> {
    const payload = { email: state.email, codUsr: 'METS' };
    const path = RegisterApiPath.EMAIL;
    const url = this.getUrl(path, 'middlewarePublicServices');
    const headers = this.base.getPublicHeaders();
    const params = this.getHttpParamas(state);
    return this.http.post<ValidateEmailResponse>(url, payload, { ...headers, params })
      .pipe(
        map(data => {
          data.validEmail = data.validEmail === 'S';
          data.status = 200;
          return data;
        }),
        this.catchError,
        spinner(RegisterLoadingId.VALIDATE_EMAIL));
  }

  getUrlTeCuidamos(): Observable<{ url: string; }> {
    const url = 'Fake url';
    return of({ url });
  }

  validateInitialData(
    state: Partial<RegisterState>,
    attemps?: RegisterAttempts,
    advertisingConsent = false,
    ricProfileRequest?: RegisterRicProfileRequest
  ): Observable<RegisterResponse> {
    const payload = this.getInitialDataPayload(state, attemps, advertisingConsent, ricProfileRequest);
    const path = RegisterApiPath.USER;
    const baseUrl = this.getUrl(path, 'middlewarePublicServices', '2.0');
    const headers = this.base.getPublicHeaders();
    return this.http.post<RegisterResponse>(baseUrl, payload, { ...headers })
      .pipe(
        map(data => {
          data.status = 200;
          return data;
        }),
        this.catchError,
        spinner(RegisterLoadingId.VALIDATE_INITIAL_DATA));
  }

  decryptHash(hash: string): Promise<Partial<RegisterUserHttpResponse>> {
    const path = `${RegisterApiPath.HASH}${hash}`;
    const url = this.getUrl(path, 'middlewarePublicServices');
    const params: HttpParams = this.getHttpParamas(null);
    const headers = this.base.getPublicHeaders();

    return this.http.get(url, { ...headers, params }).
      pipe(
        catchError(() => of({})),
        spinner(RegisterLoadingId.DECYPHER_HASH),
      ).toPromise();
  }

  getOnelineCampaignUserData(data: { password: string, documentNumber: string; }): Observable<Partial<RegisterUserHttpResponse>> {
    const url = this.mmHelper.getCommonServiceUrl('JWTRegisterUser', RegisterApiPath.LOGIN, '2.0');

    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('MMOBILE_JWT_ACCEPT_TOKEN', 'header')
      .append('X-NGX-CACHE-INTERCEPTOR', 'clear-cache')
      .append('X-Version-Id', this.mmHelper.getVersionId());


    const params = new HttpParams()
      .set('portalId', REGISTER_PORTAL_ID)
      .set('entityId', REGISTER_ENTITY_ID)
      .set('groupId', REGISTER_GROUP_CODE)
      .set('originId', this.ro.getOrigin().toString())
      .set('serviceCode', this.getServiceCode({ srv: null }));

    const payload = this.getOnlineCampaignPayload(data);
    return this.http.post<Partial<RegisterUserHttpResponse>>(url, payload, { headers, params, observe: 'response' })
      .pipe(
        map((response: HttpResponse<Partial<RegisterUserHttpResponse>>) => {
          return { ...response.body, token: response.headers.get('mmobile_jwt_access_token') };
        }),
        this.catchError,
        spinner(RegisterLoadingId.ONLINE_CAMPAIGN));
  }

  sendRegisterRequest(state: Partial<RegisterState>): Observable<RegisterResponse> {
    const payload = this.getRegisterUserPayload(state);
    const path = 'user';
    const baseUrl = this.mmHelper.getCommonServiceUrl('middlewarePrivateRegisterUserServices', '/services/j', '2.0');
    const url = `${baseUrl}/${path}`;
    const headers = this.getAuthHeaders(state.token);
    this.base.getPublicHeaders();
    return this.http.post<RegisterResponse>(url, payload, { headers })
      .pipe(
        map(data => {
          data.status = 200;
          return data;
        }),
        this.catchError,
        spinner(RegisterLoadingId.SEND_REQUEST));
  }

  getToken(state: Partial<RegisterState>): Observable<string> {
    const url = this.mmHelper.getCommonServiceUrl('registerIdentification', '/services/p');
    const headers = this.getPublicAuthHeaders(state);
    const params = new HttpParams().set('documentNumber', state.documentNumber);
    return this.http.get<string>(url, { ...headers, params });
  }

  validatePin(
    pinCode: string,
    phoneNumber: string
  ): Observable<RegisterValidatePinSMSResponse> {
    const pinUrl = this.mmobileHelper.getValidatePinUrl();
    const headers = this.base.getPublicHeaders();
    const body = {
      pin: pinCode,
      userId: phoneNumber,
      appKey: APP_KEY_PIN_SMS,
      versionKey: '1.0'
    };

    return this.http.post<RegisterValidatePinSMSResponse>(pinUrl, body, headers)
      .pipe(
        map(data => {
          data.status = 200;
          return data;
        }),
        catchError((err: HttpErrorResponse) => {
          return of({
            businessId: null,
            token: null,
            status: err.status,
            error: err,
          });
        }),
        spinner(RegisterLoadingId.VALIDATE_EMAIL));
  }

  getPinSMS(user: PatrimonialIdentification) {
    const pinUrl = this.mmobileHelper.getRequirePinUrl();
    const headers = this.base.getPublicHeaders();
    const registerOperationContext = {
      roGroupingCode: REGISTER_GROUP_CODE,
      roOrigin: this.ro.getOrigin().toString(),
      roProductCode: '',
      roProductId: '',
      roOperationCodePrefix: '8',
      roEntityCode: REGISTER_ENTITY_ID,
      roDocumentNumber: user.documentNumber
    };

    const body = {
      businessId: user.documentNumber,
      userType: SMS,
      userId: user.phoneNumber,
      appKey: APP_KEY_PIN_SMS,
      versionKey: '1.0',
      language: isAndroidApp(this.platform)
        ? LANGUAGE.android
        : LANGUAGE.default,
      validationData: {
        phone: user.phoneNumber,
        registerOperationContext
      }
    };

    return this.http.post(pinUrl, body, headers)
      .pipe(spinner(RegisterLoadingId.GET_PIN));
  }

  validatePinEmail(
    state: Partial<RegisterState>,
    pinCode: string
  ): Observable<RegisterValidatePinEmailResponse> {
    const path = 'clientsArea/clients/validation/otp';
    const baseUrl = this.getUrl(path, 'middlewarePublicServices', '2.0');
    const headers = this.base.getPublicHeaders();
    const body: any = {
      token: state.tokenValidation,
      otp: pinCode,
      onlineRegistration: true,
      roData: {
        portalId: REGISTER_PORTAL_ID,
        entityId: REGISTER_ENTITY_ID,
        groupId: REGISTER_GROUP_CODE,
        serviceCode: this.getServiceCode(state),
        originId: this.ro.getOrigin().toString()
      }
    };

    return this.http.post<RegisterValidatePinEmailResponse>(baseUrl, body, headers)
      .pipe(
        map(data => {
          data.status = 200;
          return data;
        }),
        catchError((err: HttpErrorResponse) => {
          return of({
            code: null,
            valid: null,
            message: null,
            token: null,
            status: err.status,
            error: err
          });
        }),
        spinner(RegisterLoadingId.VALIDATE_EMAIL));
  }

  getPinEmail(state: Partial<RegisterState>, emailVerification: boolean) {
    let path = 'clientsArea/clients/otp/email';
    if (emailVerification) { path = path + '/onboarding'; }
    const baseUrl = this.getUrl(path, 'middlewarePublicServices', '2.0');
    const headers = this.base.getPublicHeaders();

    const body: any = {
      token: state.tokenValidation,
      onlineRegistration: true,
      registerType: 'METS',
      roData: {
        portalId: REGISTER_PORTAL_ID,
        entityId: REGISTER_ENTITY_ID,
        groupId: REGISTER_GROUP_CODE,
        serviceCode: this.getServiceCode(state),
        originId: this.ro.getOrigin().toString()
      }
    };
    if (emailVerification) { body.email = state.email; }

    return this.http.post(baseUrl, body, headers)
      .pipe(spinner(RegisterLoadingId.GET_PIN));
  }

  downloadContractPDF(state: Partial<RegisterState>) {
    const url = this.mmHelper.getCommonServiceUrl('middlewarePrivateRegisterUserServices', '/services/j') + '/user/contract';
    const headers = this.getAuthHeaders(state.token);
    this.http.get(url, { headers })
      .pipe(spinner(RegisterLoadingId.DOWNLOAD_PDF))
      .subscribe((base64: string) => {
        this.fileHelper.getDocumentFromBase64(
          base64,
          'application/pdf',
          `${this.translate.instant('REGISTER.CONTRACT_PDF_NAME')}.pdf`
        ).then((doc) => {
          this.fileHelper.open(doc);
        });
      });
  }

  getServiceCode(state: Partial<RegisterState>): string {
    if (state.srv) {
      return state.srv;
    }

    return this.ctx.isApp
      ? RegisterServiceCodes.APP
      : RegisterServiceCodes.WEB;
  }

  getUserData(state: Partial<RegisterState>): Observable<GetUserDataResponse> {
    const url = this.mmHelper.getCommonServiceUrl('middlewarePrivateRegisterUserServices', '/services/j', '2.0') + '/profile';
    const headers = this.getAuthHeaders(state.token);
    return this.http.get<GetUserDataResponse>(url, { headers })
      .pipe(spinner(RegisterLoadingId.GET_USER_DATA_ONBOARDING));
  }

  postUserDataOnboarding(state: Partial<RegisterState>, user: GetUserDataResponse): Observable<PostUserDataResponse> {
    const url = this.mmHelper.getCommonServiceUrl('middlewarePrivateRegisterUserServices', '/services/j', '2.0') + '/profile';
    const body = this.getPayloadPostUserOnboarding(user);
    const headers = this.getAuthHeaders(state.token);
    return this.http.post<PostUserDataResponse>(url, body, { headers })
      .pipe(
        map(() => {return {status: 200};}),
        this.catchError,
        spinner(RegisterLoadingId.USER_DATA_ONBOARDING));
  }

  postUserDataOnboardingLogged(user): Observable<PostUserDataResponse> {
    const path = 'middlewarePrivateProfile/register';
    const baseUrl = this.mmobileHelper.getUserDataUrl();
    const url = `${baseUrl}/${path}`;
    const body = this.getPayloadPostUserOnboarding(user);
    const headers = this.getAuthHeaders(user?.token);

    return this.http.post<PostUserDataResponse>(url, body, { headers })
      .pipe(
        map(() => {return {status: 200};}),
        this.catchError,
        spinner(RegisterLoadingId.USER_DATA_ONBOARDING));
  }

  private getPayloadPostUserOnboarding(user) {
    const payload = {
      address: {
        street: user?.street,
        streetType: user?.streetType,
        city: user?.locality,
        number: user?.streetNumber,
        stair: user?.stairs,
        floor: user?.floor,
        door: user?.door,
        portal: user?.doorway,
        postalCode: user?.postalCode,
        additionalInfo: user?.addressComplement
      },
      birthdate: moment(user.birthdatePicker).format('YYYY-MM-DD'),
      email: user.email?.trim(),
      phones: [user.phone.toString()?.trim()],
      postalCode: user.postalCode,
      mcaOnboarding: 'S',
      appName: 'METS',
      regOpe: {
        portal: REGISTER_PORTAL_ID,
        origen: this.ro.getOrigin().toString(),
        entidad: REGISTER_ENTITY_ID,
        agrupacion: REGISTER_GROUP_CODE
      }
    };

    Object.keys(payload.address).forEach((key) => {
      if (payload.address[key] === null || payload.address[key] === undefined || payload.address[key].length === 0) { delete payload.address[key]; }
    });

    return payload;
  }

  private getIncomingCallRAPayload(state: Partial<RegisterState>) {
    return {
      name: state.name,
      surname1: state.lastname1,
      surname2: state.lastname2,
      documentTypeId: state.documentType,
      documentNumber: state.documentNumber,
      phone: state.phone,
      secondPhone: '',
      email: state.email
    };
  }

  private getOnlineCampaignPayload(data) {
    return {
      username: data.documentNumber,
      password: data.password
    };
  }

  private getRegisterUserPayload(state: Partial<RegisterState>) {
    const payload = {
      documentType: state.documentType,
      documentNumber: state.documentNumber.toString()?.trim(),
      name: state.name,
      surname1: state.lastname1,
      surname2: state.lastname2?.trim(),
      email: state.email ? state.email?.trim() : state?.emailRes,
      mobile: state.phone ? state.phone?.toString()?.trim() : state?.phoneRes,
      birthDate: moment(state.birthdate).format('DD/MM/YYYY'),
      gender: RegisterGenderOptions[state.gender].toString(),
      password: state.password?.trim(),
      codViaType: state.streetType,
      streetTypeCode: state.streetType,
      postalCode: state.postalCode,
      province: this.addressHelper.getProvinceCodeFromPostalCode(state?.postalCode),
      locality: state.locality,
      streetName: state.street,
      streetNumber: state.streetNumber?.toString(),
      flat: state.floor?.toString(),
      portal: state.doorway?.toString(),
      stairs: state.stairs,
      door: state.door || state.doorway,
      addressComplement: state.addressComplement,
      teCuidamosRegister: state?.hashTC ? true : state.MtC || false,
      encryptedHashTC: state.hashTC,
      aciConsentRegister: state.serviceConditions,
      teCuidamosConsentRegister: state.MtC ? state.privacyPolicies : false,
      mcaOnboarding: state?.hashTC ? true : state.MtC || false,
      origin: 'METS',
      onlineRegistration: !state.onlineCampaign,
      roData: {
        portalId: REGISTER_PORTAL_ID,
        entityId: REGISTER_ENTITY_ID,
        groupId: REGISTER_GROUP_CODE,
        serviceCode: this.getServiceCode(state),
        originId: this.ro.getOrigin().toString()
      },
      channel: state.channel,
      subChannel: state.subchannel,
      advertisingConsent: !!state.receiveInfo
    };

    Object.keys(payload).forEach((key) => {
      if (payload[key] === null || payload[key] === undefined || payload[key].length === 0) { delete payload[key]; }
    });
    return payload;
  }

  private getDocumentType(state: Partial<RegisterState>): string {
    return Object.values(RegisterDocumentTypeOptions).find(t => t === Number(state.documentType)).toString();
  }

  private getInitialDataPayload(
    state: Partial<RegisterState>,
    attemps?: RegisterAttempts,
    advertisingConsent = false,
    ricProfile?: RegisterRicProfileRequest
  ) {

    let payload = {
      documentType: this.getDocumentType(state),
      documentNumber: state.documentNumber?.trim(),
      email: state.email?.trim(),
      mobile: state.phone?.toString(),
      roData: {
        portalId: REGISTER_PORTAL_ID,
        entityId: REGISTER_ENTITY_ID,
        groupId: REGISTER_GROUP_CODE,
        serviceCode: this.getServiceCode(state),
        originId: this.ro.getOrigin().toString()
      }
    };

    const attemptNumber = this.getAttempts(attemps);
    const ibanNumber = state.IBAN?.toString();

    if (attemptNumber && ibanNumber) {
      payload['attemptNumber'] = attemptNumber;
      payload['ibanNumber'] = ibanNumber;
    }

    if (advertisingConsent) {
      payload['advertisingConsent'] = state.receiveInfo ? 'S' : 'N';
    }

    if (state?.hashTC && !state.secondAuth) {
      delete payload?.mobile;
    }

    if (ricProfile) {
      payload = { ...payload, ...ricProfile};
    }

    return payload;
  }

  private getAttempts(attemps?: RegisterAttempts): string {
    const attemp = {
      3: '1',
      2: '2',
      1: '3'
    };

    return attemps ? attemp[attemps.value] : null;
  }

  private getUrl(path: string, middleware: string, version = '1.0'): string {
    const baseUrl = this.mmobileHelper.getCommonServiceUrl(middleware, null, version);
    return `${baseUrl}/${path}`;
  }



  private getHttpParamas(state: Partial<RegisterState>): HttpParams {
    return new HttpParams()
      .set('roEntityCode', REGISTER_ENTITY_ID)
      .set('roGroupingCode', REGISTER_GROUP_CODE)
      .set('roOrigin', this.ro.getOrigin().toString())
      .set('roProductId', '')
      .set('roOperationCodePrefix', '8')
      .set('roDocumentNumber', state?.documentNumber || '')
      .set('roEmail', state?.email || '');
  }

  private getPublicAuthHeaders(
    state: Partial<RegisterState>,
    opts?: { cacheResponse: boolean; clearCache?: boolean; }
  ) {
    let headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('Authorization', 'Basic ' + btoa(`${state.phone.toString()}:${state.pinToken}`))
      .append('X-Version-Id', this.mmHelper.getVersionId());
    headers = this.base.setCacheInterceptorHeader(headers, opts);
    return { headers };
  }

  private getAuthHeaders(token: string) {
    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('X-Authorization', token)
      .append('X-Version-Id', this.mmHelper.getVersionId());

    return headers;
  }
}


